import { makeAutoObservable } from "mobx";
import { IInputValitationResponse } from "../../../hooks/input-validation-hook";
import { IInstallment } from "../../../models/Plan";
import { IChangeable } from "../../../stores/types";
import PlanInstallmentEditorStore, { IInstallmentChanges } from "./plan-installment-editor-store";
import SubscriptionPlanEditorStore, { PLAN_CHARGE_TYPES } from "./subscription-plan-editor-store";

export enum INSTALLMENTS_BREAKDOWN_TYPES {
    EVEN = 'even',
    CUSTOM = 'custom',
    ABSOLUTE = 'absolute'
}

/** this interface contains local properties - those that are not part of model, we need it to detect changes */
interface IInstallmentListManagerProps {
    numberOfEvenInstallments: number;
    installmentsBreakDownType: INSTALLMENTS_BREAKDOWN_TYPES;
}
type IInstallmentsListCache = {
    [key in INSTALLMENTS_BREAKDOWN_TYPES]?: PlanInstallmentEditorStore[];
}

interface IInstallmentListManagerPropsChanges extends Partial<IInstallmentListManagerProps>{}

export class InstallmentListManager implements IChangeable<IInstallment[]> {
    private _planEditor: SubscriptionPlanEditorStore;
    private _installmentsList: PlanInstallmentEditorStore[] = [];
    private _cachedInstallments: IInstallmentsListCache = {};
    
    private _properties!: IInstallmentListManagerProps;
    private _propertiesChanges: IInstallmentListManagerPropsChanges = {};


    constructor(planEditor: SubscriptionPlanEditorStore, modelList?: IInstallment[]) {
        makeAutoObservable(this, {}, {autoBind: true});

        this._planEditor = planEditor;
        this._installmentsList = (modelList ?? []).map(model => new PlanInstallmentEditorStore(this._planEditor, model));

        let installmentsBreakDownType: INSTALLMENTS_BREAKDOWN_TYPES = INSTALLMENTS_BREAKDOWN_TYPES.ABSOLUTE;
        const hasAbsoluteInstallments = this._installmentsList.some(installment => isSet(installment.absoluteChargeDate));
        const hasEvenInstallments = this._installmentsList.some(installment => isSet(installment.monthsSinceLastInstallment));
        const hasCustomInstallments = this._installmentsList.some(installment => isSet(installment.daysSinceLastInstallment));

        if(hasEvenInstallments) {
            installmentsBreakDownType = INSTALLMENTS_BREAKDOWN_TYPES.EVEN;
        }

        if(hasCustomInstallments) {
            installmentsBreakDownType = INSTALLMENTS_BREAKDOWN_TYPES.CUSTOM;
        }

        if(hasAbsoluteInstallments) {
            installmentsBreakDownType = INSTALLMENTS_BREAKDOWN_TYPES.ABSOLUTE;
        }

        this._properties = {
            installmentsBreakDownType: installmentsBreakDownType,
            numberOfEvenInstallments: this._installmentsList.length,
        };
    }

    public getInstallments(): PlanInstallmentEditorStore[] {
        return this._installmentsList.filter(inst => !inst.isDeleted);
    }

    public setChanges(changesList: IInstallmentChanges[] = []) {
        this._installmentsList = changesList.map(installment => {
            const installmentStore = new PlanInstallmentEditorStore(this._planEditor);
            installmentStore.setChanges(installment);
            return installmentStore;
        });

        this._propertiesChanges.installmentsBreakDownType = this._installmentsList.some(installment => isSet(installment.monthsSinceLastInstallment)) 
            ? INSTALLMENTS_BREAKDOWN_TYPES.EVEN
            : INSTALLMENTS_BREAKDOWN_TYPES.CUSTOM;
        
        this._propertiesChanges.numberOfEvenInstallments = this._installmentsList.length;
    }

    public isInEditMode(): boolean {
        return this.getInstallments().some(inst => inst.isInEditMode());
    }

    public removeInstallment(instalmentStore: PlanInstallmentEditorStore): void {
        instalmentStore.setDeleted();
        /** remove installment from the list that was created and deleted on client */
        /** TODO: simplify - remove any installment from the list, remove the setDeleted */
        if (!instalmentStore.hasServerModel() || instalmentStore.monthsSinceLastInstallment !== null) {
            this._installmentsList = this._installmentsList.filter(inst => !inst.isDeleted);
        }
        this._setImmediateInstallment();
    }

    public addCustomInstallment(): void {
        const newInstallment = new PlanInstallmentEditorStore(this._planEditor);
        newInstallment.setInstallmentAmount(1000);
        newInstallment.setDaysSinceLastInstalment(31);        
        newInstallment.setInEditMode(true);
        this._installmentsList.push(newInstallment);
        this._setImmediateInstallment();
    }

    public addAbsoluteInstallment(): void {
        const newInstallment = new PlanInstallmentEditorStore(this._planEditor);
        newInstallment.setInstallmentAmount(1000);
        newInstallment.setInstallmentDefaultDate();
        newInstallment.setInEditMode(true);
        this._installmentsList.push(newInstallment);
    }

    private _setImmediateInstallment(): void {
        const firstInstallment = this.getInstallments()[0];
        if (!firstInstallment || isSet(firstInstallment.absoluteChargeDate)) {
            return;
        }
        /** enforcing first installment to be immediate */
        if (firstInstallment.daysSinceLastInstallment !== 0) {
            firstInstallment.setDaysSinceLastInstalment(0);
            firstInstallment.commitChanges();
        }
    }

    public get installmentBreakdownType(): INSTALLMENTS_BREAKDOWN_TYPES | undefined {
        return this._propertiesChanges.installmentsBreakDownType ?? this._properties.installmentsBreakDownType;
    }

    public changeInstallmentsBreakdownType(v: INSTALLMENTS_BREAKDOWN_TYPES): void {
        if(v === this.installmentBreakdownType) {
            return;
        }

        /** Save current installments setup to cache */
        switch (this.installmentBreakdownType) {
            case INSTALLMENTS_BREAKDOWN_TYPES.CUSTOM:
                this._cachedInstallments[INSTALLMENTS_BREAKDOWN_TYPES.CUSTOM] = this._installmentsList;
                break;
            case INSTALLMENTS_BREAKDOWN_TYPES.ABSOLUTE:
                this._cachedInstallments[INSTALLMENTS_BREAKDOWN_TYPES.ABSOLUTE] = this._installmentsList;
                break;
        }
        this._propertiesChanges.installmentsBreakDownType = v;

        if (v === INSTALLMENTS_BREAKDOWN_TYPES.EVEN) {
            this._generateEvenInstallmentsIfNeeded();
            return;
        }

        /** Retrieve installments from cache */
        this._installmentsList = this._cachedInstallments[v] ?? [];
    }

    public getNumberOfEvenInstallments(): number {
        return this._propertiesChanges.numberOfEvenInstallments ?? this._properties.numberOfEvenInstallments;
    }

    public setNumberOfEvenInstallments(v: number): void {
        this._propertiesChanges.numberOfEvenInstallments = v;
        this._generateEvenInstallmentsIfNeeded();
    }

    public validateEvenInstallmentsCount(): IInputValitationResponse {
        /** for even installments the minimum amount allowed is 2 and maximum is plans duration in months */
        if (this._planEditor.type === PLAN_CHARGE_TYPES.LONG_TERM && this.installmentBreakdownType === INSTALLMENTS_BREAKDOWN_TYPES.EVEN) {
            if (this.getNumberOfEvenInstallments() < 2 || this.getNumberOfEvenInstallments() > (this._planEditor.period ?? 0)) {
                return {
                    isValid: false,
                    messages: ['allowed number of even installments should be between 2 and period (months)'],
                }
            }
        }
        return {
            isValid: true,
            messages: [],
        }
    }

    /** TODO: temporary public - to fix */
    public _generateEvenInstallmentsIfNeeded(): void {
        const isNumberSet = (v: undefined|number|null): boolean => isSet(v) && (v as number) > 0;

        /** first make sure all requirements are met to generate even installmetns */
        if (this._planEditor.type !== PLAN_CHARGE_TYPES.LONG_TERM) {
            return;
        }
        if (this.installmentBreakdownType !== INSTALLMENTS_BREAKDOWN_TYPES.EVEN) {
            return;
        }
        if (!isNumberSet(this._planEditor.period)) {
            return;
        }
        if (!isNumberSet(this._planEditor.price)) {
            return;
        }
        if (!isNumberSet(this.getNumberOfEvenInstallments())) {
            return;
        }

        /** dropping all installments before generating new ones */
        this._installmentsList = [];

        for (let i = 0; i < this.getNumberOfEvenInstallments(); i++) {
            this._addEvenInstallment(i === 0);
        }
    }

    private _addEvenInstallment(isFirstInstallment: boolean): void {
        const monthsSinceLastInstallment = Number(((this._planEditor.period ?? 1) / (this.getNumberOfEvenInstallments() ?? 1)).toFixed(2));
        const evenInstallmentPrice = Number((this.getEvenInstallmentsPrice() ?? ((this._planEditor.price ?? 1) / this.getNumberOfEvenInstallments())).toFixed(2));

        /** this can be imporved by using setChanges method in installment store */
        const newEvenInstallment = new PlanInstallmentEditorStore(this._planEditor);
        newEvenInstallment.setInstallmentAmount(evenInstallmentPrice);
        newEvenInstallment.setMonthsSinceLastInstallment(isFirstInstallment ? 0 : monthsSinceLastInstallment);
        newEvenInstallment.commitChanges();

        this._installmentsList.push(newEvenInstallment);
    }

    public getEvenInstallmentsPrice(): number|undefined {
        /** TODO: this should simply be (price / num of installments)  */
        return this.getInstallments().find(installment => installment.monthsSinceLastInstallment !== null)?.installmentAmount;
    }

    discardChanges(): void {
        /** this store should simply be recreated from model when changes are discarded, no need to implement this method */
        throw new Error('not implemented');
    }

    getUpdatedModel(): IInstallment[] {
        return this._planEditor.type === PLAN_CHARGE_TYPES.LONG_TERM
            ? this.getInstallments().map((i) => i.getUpdatedModel())
            : [];
    }

    hasChanges(): boolean {
        const installmentsHasChanges = this._planEditor.type === PLAN_CHARGE_TYPES.LONG_TERM && this._installmentsList.some(s => s.hasChanges());
        const hasLocalPropsChanges = Object.keys(this._propertiesChanges).length > 0;
        return installmentsHasChanges || hasLocalPropsChanges;
    }
}



function isSet(v: any): boolean {
    return v !== null && v !== undefined;
}